import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { toast } from "sonner";
import { ordersApi } from "../utils/api";
import { formatAttributionValue, formatLocation, getAttribution } from "../utils/attribution";

function formatDateTime(input) {
  if (!input) return "—";
  try {
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return String(input);
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(d)
      .replace(/, (\d{2}:\d{2})/, ", $1");
  } catch {
    return String(input);
  }
}

function formatCurrency(amount, currency = "INR") {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount ?? 0);
  } catch {
    return `${currency} ${amount ?? 0}`;
  }
}

function getStatusBadge(status) {
  const lower = String(status || "").toLowerCase();
  if (lower === "success") {
    return <Badge className="bg-green-600 text-white hover:bg-green-600">Success</Badge>;
  }
  if (lower === "failed") {
    return <Badge variant="destructive">Failed</Badge>;
  }
  if (lower === "pending") {
    return <Badge className="bg-amber-500 text-white hover:bg-amber-500">Pending</Badge>;
  }
  return <Badge variant="secondary">{status || "Unknown"}</Badge>;
}

export function Orders() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [appSource, setAppSource] = useState("");
  const [appId, setAppId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("");
  const [campaign, setCampaign] = useState("");
  const [country, setCountry] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [city, setCity] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await ordersApi.getPaymentOrders(currentPage, limit, {
        payment_status: paymentStatus,
        app_source: appSource,
        app_id: appId,
        search,
        from_date: fromDate,
        to_date: toDate,
        min_amount: minAmount,
        max_amount: maxAmount,
        sort_by: sortBy,
        sort_order: sortOrder,
        source,
        medium,
        campaign,
        country,
        state: stateFilter,
        city,
      });

      const data = response?.data ?? {};
      setOrders(data.orders ?? []);
      setTotalPages(data.pagination?.totalPages ?? 0);
      setTotal(data.pagination?.total ?? 0);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error(error?.message || "Failed to fetch orders");
      setOrders([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await ordersApi.getPaymentInsights({
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        app_source: appSource || undefined,
        app_id: appId || undefined,
      });
      setSummary(response?.data?.summary ?? null);
    } catch (error) {
      console.error("Error fetching insights:", error);
      setSummary(null);
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [
    currentPage,
    limit,
    paymentStatus,
    appSource,
    appId,
    fromDate,
    toDate,
    minAmount,
    maxAmount,
    sortBy,
    sortOrder,
    search,
    source,
    medium,
    campaign,
    country,
    stateFilter,
    city,
  ]);

  useEffect(() => {
    fetchInsights();
  }, [fromDate, toDate, appSource, appId]);

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(Number(value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setPaymentStatus("all");
    setAppSource("");
    setAppId("");
    setFromDate("");
    setToDate("");
    setMinAmount("");
    setMaxAmount("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setSearch("");
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const safeTotalPages = Math.max(totalPages, 1);
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages || 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || totalPages <= 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1 || totalPages <= 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || totalPages <= 1}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages <= 1}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
        <p className="text-sm text-muted-foreground order-2 sm:order-1">
          Page {currentPage} of {safeTotalPages}
          {total > 0 && <span className="hidden sm:inline"> · {total} total</span>}
        </p>
        <div className="flex flex-wrap justify-center gap-2 order-1 sm:order-2">
          {pages}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="mb-2">Orders</h1>
        <p className="text-muted-foreground">
          Manage payment orders and review payment insights
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Orders</p>
            <h2 className="text-2xl font-semibold mt-1">
              {insightsLoading ? "..." : summary?.total_orders ?? 0}
            </h2>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Revenue</p>
            <h2 className="text-2xl font-semibold mt-1">
              {insightsLoading
                ? "..."
                : formatCurrency(summary?.total_revenue ?? 0, "INR")}
            </h2>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Success Rate</p>
            <h2 className="text-2xl font-semibold mt-1">
              {insightsLoading
                ? "..."
                : `${Number(summary?.success_rate_percent ?? 0).toFixed(1)}%`}
            </h2>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Avg Success Order</p>
            <h2 className="text-2xl font-semibold mt-1">
              {insightsLoading
                ? "..."
                : formatCurrency(summary?.avg_success_order_value ?? 0, "INR")}
            </h2>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
            <div className="flex-1">
              <label className="block text-sm mb-2 text-foreground">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by payment id/order id/user..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 border border-gray-300"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters((prev) => !prev)}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>

          {showFilters ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-sm mb-2 text-foreground">Payment Status</label>
              <Select
                value={paymentStatus}
                onValueChange={(value) => {
                  setPaymentStatus(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="border border-gray-300">
                  <SelectValue placeholder="Payment Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm mb-2 text-foreground">App Source</label>
              <Input
                type="text"
                value={appSource}
                onChange={(e) => {
                  setAppSource(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="App Source (e.g. pingowner-web)"
                className="border border-gray-300"
              />
            </div>

            <div>
              <label className="block text-sm mb-2 text-foreground">App ID</label>
              <Input
                type="text"
                value={appId}
                onChange={(e) => {
                  setAppId(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="App ID (e.g. pingowner)"
                className="border border-gray-300"
              />
            </div>

            <div>
              <label className="block text-sm mb-2 text-foreground">Rows</label>
              <Select
                value={String(limit)}
                onValueChange={handleLimitChange}
              >
                <SelectTrigger className="border border-gray-300">
                  <SelectValue placeholder="Rows" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm mb-2 text-foreground">From Date</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">To Date</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">Min Amount</label>
              <Input
                type="number"
                min="0"
                value={minAmount}
                onChange={(e) => {
                  setMinAmount(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Min Amount"
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">Max Amount</label>
              <Input
                type="number"
                min="0"
                value={maxAmount}
                onChange={(e) => {
                  setMaxAmount(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Max Amount"
                className="border border-gray-300"
              />
            </div>

            <div>
              <label className="block text-sm mb-2 text-foreground">Sort By</label>
              <Select
                value={sortBy}
                onValueChange={(value) => {
                  setSortBy(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="border border-gray-300">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="createdAt">Created At</SelectItem>
                  <SelectItem value="total_amount">Amount</SelectItem>
                  <SelectItem value="payment_verified_at">Verified At</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm mb-2 text-foreground">Sort Order</label>
              <Select
                value={sortOrder}
                onValueChange={(value) => {
                  setSortOrder(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="border border-gray-300">
                  <SelectValue placeholder="Sort Order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Descending</SelectItem>
                  <SelectItem value="asc">Ascending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Attribution Filters */}
            <div>
              <label className="block text-sm mb-2 text-foreground">Source</label>
              <Input
                value={source}
                onChange={(e) => { setSource(e.target.value); setCurrentPage(1); }}
                placeholder="e.g. google"
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">Medium</label>
              <Input
                value={medium}
                onChange={(e) => { setMedium(e.target.value); setCurrentPage(1); }}
                placeholder="e.g. cpc"
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">Campaign</label>
              <Input
                value={campaign}
                onChange={(e) => { setCampaign(e.target.value); setCurrentPage(1); }}
                placeholder="e.g. summer"
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">Country</label>
              <Input
                value={country}
                onChange={(e) => { setCountry(e.target.value); setCurrentPage(1); }}
                placeholder="e.g. India"
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">State</label>
              <Input
                value={stateFilter}
                onChange={(e) => { setStateFilter(e.target.value); setCurrentPage(1); }}
                placeholder="e.g. Gujarat"
                className="border border-gray-300"
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-foreground">City</label>
              <Input
                value={city}
                onChange={(e) => { setCity(e.target.value); setCurrentPage(1); }}
                placeholder="e.g. Ahmedabad"
                className="border border-gray-300"
              />
            </div>
            
            <div className="col-span-full pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setSource(""); setMedium(""); setCampaign("");
                  setCountry(""); setStateFilter(""); setCity("");
                  setPaymentStatus("all"); setAppSource(""); setAppId("");
                  setFromDate(""); setToDate(""); setMinAmount(""); setMaxAmount("");
                  setSortBy("createdAt"); setSortOrder("desc");
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>

            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Order List ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading orders...</div>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">No orders found</div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Custom Order ID</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Address Type</TableHead>
                    <TableHead>Delivery Address</TableHead>
                    <TableHead>Attribution</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Verified At</TableHead>
                    <TableHead>Razorpay Order</TableHead>
                    <TableHead>Razorpay Payment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order._id}>
                      <TableCell className="font-medium">
                        {order.custom_order_id || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="min-w-[180px]">
                          <p className="font-medium">
                            {order.user?.name || order.delivery_address?.name || "Guest"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {order.user?.mobile || order.delivery_address?.phone || "—"}
                          </p>
                          {order.user?.email ? (
                            <p className="text-xs text-muted-foreground">
                              {order.user.email}
                            </p>
                          ) : null}
                          <p className="text-xs text-muted-foreground">
                            {order.user_id || "—"}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatCurrency(order.total_amount, order.currency || "INR")}
                      </TableCell>
                      <TableCell>{getStatusBadge(order.payment_status)}</TableCell>
                      <TableCell>{order.delivery_address?.address_type || "—"}</TableCell>
                      <TableCell className="max-w-[260px] truncate">
                        {order.delivery_address?.address || "—"}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const attribution = getAttribution(order);
                          const touch = attribution?.latestTouch || attribution?.firstTouch;
                          return touch ? (
                            <div className="min-w-[150px] text-sm">
                              <p>{formatAttributionValue(touch.source)} / {formatAttributionValue(touch.medium)}</p>
                              <p className="text-muted-foreground truncate max-w-[180px]" title={formatAttributionValue(touch.campaign)}>
                                {formatAttributionValue(touch.campaign)}
                              </p>
                            </div>
                          ) : "—";
                        })()}
                      </TableCell>
                      <TableCell className="min-w-[170px]">
                        {formatLocation(getAttribution(order)?.location)}
                      </TableCell>
                      <TableCell>{formatDateTime(order.createdAt)}</TableCell>
                      <TableCell>{formatDateTime(order.payment_verified_at)}</TableCell>
                      <TableCell>{order.razorpay_order_id || "—"}</TableCell>
                      <TableCell>{order.razorpay_payment_id || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {renderPagination()}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
